// ============================================
// MedicinePage.jsx
// ULTRA RESPONSIVE NEXT GEN UI
// REAL IMAGE URL FROM BACKEND
// MOBILE + DESKTOP OPTIMIZED
// ============================================

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

import {
  FaPills,
  FaShoppingCart,
  FaBolt,
  FaIndustry,
  FaBox,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaCapsules,
  FaTimes,
} from "react-icons/fa";

import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

import Navbar from "../components/Navbar";
import MedicineDetailPopup from "./MedicineDetailPopup";
import { BASE_URL } from "../backend/backend.js";

// ============================================
// FALLBACK IMAGE
// ============================================

const DEFAULT_MEDICINE_IMAGE =
  "https://cdn-icons-png.flaticon.com/512/4320/4320337.png";

const MedicinePage = () => {
  const location = useLocation();

  const initialSearch =
    location.state?.searchQuery || "";

  // ============================================
  // STATES
  // ============================================

  const [items, setItems] = useState([]);

  const [quantities, setQuantities] =
    useState({});

  const [searchQuery, setSearchQuery] =
    useState(initialSearch);

  const [appliedSearch, setAppliedSearch] =
    useState(initialSearch);

  const [selectedCategory, setSelectedCategory] =
    useState("All");

  const [manufacturerFilter, setManufacturerFilter] =
    useState("");

  const [categories, setCategories] =
    useState(["All"]);

  const [showAdvancedFilters, setShowAdvancedFilters] =
    useState(false);

  const [selectedMedicine, setSelectedMedicine] =
    useState(null);

  const [cartMessage, setCartMessage] =
    useState("");

  const [page, setPage] = useState(0);

  const [hasMore, setHasMore] =
    useState(true);

  const [isLoading, setIsLoading] =
    useState(false);

  const observer = useRef();

  // ============================================
  // RESPONSIVE
  // ============================================

  const isMobile =
    window.innerWidth <= 768;

  // ============================================
  // SEARCH DEBOUNCE
  // ============================================

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== appliedSearch) {
        setAppliedSearch(searchQuery);
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ============================================
  // FETCH DATA
  // ============================================

  useEffect(() => {
    setIsLoading(true);

    const url = appliedSearch
      ? `${BASE_URL}/api/filterMedicine?name=${encodeURIComponent(
          appliedSearch
        )}&page=${page}&size=40`
      : `${BASE_URL}/api/getAllMedicine?page=${page}&size=40`;

    fetch(url, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          return {
            content: [],
            last: true,
          };
        }

        return res.json();
      })
      .then((data) => {
        const arr = data.content
          ? data.content
          : Array.isArray(data)
          ? data
          : [];

        const mapped = arr.map((med) => ({
          id: med.id,

          name:
            med.medicineName ||
            "Unknown Medicine",

          actualPrice:
            parseFloat(med.price) || 0,

          discountedPrice:
            parseFloat(
              med.descountPrice
            ) ||
            parseFloat(med.price) ||
            0,

          discountPercentage:
            parseFloat(
              med.discountPercentage
            ) || 0,

          manufacturer:
            med.manufacturerName ||
            "Unknown Manufacturer",

          type:
            med.medineType ||
            "Tablet",

          description:
            med.description ||
            "No description available",

          expiryDate:
            med.expiryDate || "N/A",

          totalPiece:
            parseFloat(
              med.totalPiece
            ) || 0,

          comp1:
            med.shortComposition1 ||
            "N/A",

          comp2:
            med.shortComposition2 ||
            "N/A",

          packSize:
            med.packSizeLabel ||
            "Standard Pack",

          isDiscontinued:
            med.isDiscontinued ===
              true ||
            med.isDiscontinued ===
              "TRUE",

          // ====================================
          // REAL IMAGE URL
          // ====================================

          image:
            med.imageUrl &&
            med.imageUrl.trim() !== ""
              ? med.imageUrl.startsWith(
                  "http"
                )
                ? med.imageUrl
                : `${BASE_URL}/${med.imageUrl}`
              : DEFAULT_MEDICINE_IMAGE,
        }));

        setItems((prev) => {
          const base =
            page === 0 ? [] : prev;

          const unique = mapped.filter(
            (n) =>
              !base.some(
                (b) => b.id === n.id
              )
          );

          const combined = [
            ...base,
            ...unique,
          ];

          setCategories([
            "All",
            ...new Set(
              combined.map(
                (i) => i.type
              )
            ),
          ]);

          return combined;
        });

        setHasMore(!data.last);

        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [page, appliedSearch]);

  // ============================================
  // FILTERING
  // ============================================

  const filteredItems = useMemo(() => {
    let filtered = [...items];

    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        (item) =>
          item.type ===
          selectedCategory
      );
    }

    if (manufacturerFilter) {
      filtered = filtered.filter(
        (item) =>
          item.manufacturer ===
          manufacturerFilter
      );
    }

    return filtered;
  }, [
    items,
    selectedCategory,
    manufacturerFilter,
  ]);

  // ============================================
  // INFINITE SCROLL
  // ============================================

  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return;

      if (observer.current)
        observer.current.disconnect();

      observer.current =
        new IntersectionObserver(
          (entries) => {
            if (
              entries[0]
                .isIntersecting &&
              hasMore
            ) {
              setPage(
                (prev) => prev + 1
              );
            }
          }
        );

      if (node)
        observer.current.observe(
          node
        );
    },
    [isLoading, hasMore]
  );

  // ============================================
  // QUANTITY
  // ============================================

  const handleQuantityChange = (
    id,
    delta
  ) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(
        (prev[id] || 1) + delta,
        1
      ),
    }));
  };

  // ============================================
  // CART
  // ============================================

  const handleAddToCart = (item) => {
    const quantity =
      quantities[item.id] || 1;

    fetch(
      `${BASE_URL}/user/saveProductInCart?id=${item.id}&quantity=${quantity}`,
      {
        method: "POST",
        credentials: "include",
      }
    )
      .then(() => {
        setCartMessage(
          `${item.name} added to cart`
        );

        setTimeout(() => {
          setCartMessage("");
        }, 2500);
      })
      .catch(() => {
        setCartMessage(
          "Failed To Add Product"
        );
      });
  };

  // ============================================
  // ORDER NOW
  // ============================================

  const orderNow = (item) => {
    alert(`Ordering ${item.name}`);
  };

  return (
    <>
      <Navbar />

      <div
        style={{
          paddingTop: isMobile
            ? "88px"
            : "100px",

          paddingBottom: "100px",

          paddingLeft: isMobile
            ? "12px"
            : "20px",

          paddingRight: isMobile
            ? "12px"
            : "20px",

          maxWidth: "1600px",
          margin: "auto",
        }}
      >
        {/* ============================================ */}
        {/* MODERN SEARCH SECTION */}
        {/* ============================================ */}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            background:
              "linear-gradient(135deg,#ffffff,#f8fbff)",

            padding: isMobile
              ? "18px"
              : "28px",

            borderRadius: isMobile
              ? "24px"
              : "32px",

            marginBottom: "35px",

            border:
              "1px solid rgba(255,255,255,0.7)",

            boxShadow:
              "0 15px 40px rgba(15,23,42,0.08)",
          }}
        >
          {/* TOP SEARCH ROW */}

          <div
            style={{
              display: "flex",

              flexDirection: isMobile
                ? "column"
                : "row",

              gap: "14px",

              alignItems: "center",
            }}
          >
            {/* SEARCH BAR */}

            <div
              style={{
                flex: 1,
                width: "100%",
                position: "relative",
              }}
            >
              <FaSearch
                style={{
                  position: "absolute",

                  top: "50%",
                  left: "20px",

                  transform:
                    "translateY(-50%)",

                  color: "#2563eb",

                  fontSize: isMobile
                    ? "16px"
                    : "18px",
                }}
              />

              <input
                value={searchQuery}
                onChange={(e) =>
                  setSearchQuery(
                    e.target.value
                  )
                }
                placeholder="Search tablets, syrup, capsules..."

                style={{
                  width: "100%",

                  height: isMobile
                    ? "56px"
                    : "64px",

                  padding:
                    "0 55px 0 55px",

                  borderRadius: "999px",

                  border:
                    "2px solid #dbeafe",

                  outline: "none",

                  fontSize: isMobile
                    ? "14px"
                    : "16px",

                  fontWeight: "600",

                  background:
                    "#ffffff",

                  transition:
                    "all 0.3s ease",

                  boxShadow:
                    "0 8px 24px rgba(37,99,235,0.08)",
                }}
              />

              {/* CLEAR BUTTON */}

              {searchQuery && (
                <button
                  onClick={() =>
                    setSearchQuery("")
                  }
                  style={{
                    position: "absolute",

                    right: "18px",
                    top: "50%",

                    transform:
                      "translateY(-50%)",

                    border: "none",

                    background:
                      "#eff6ff",

                    width: "30px",
                    height: "30px",

                    borderRadius:
                      "50%",

                    cursor: "pointer",

                    display: "flex",
                    alignItems:
                      "center",

                    justifyContent:
                      "center",

                    color: "#2563eb",
                  }}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* FILTER BUTTON */}

            <motion.button
              whileTap={{
                scale: 0.95,
              }}
              whileHover={{
                scale: 1.02,
              }}
              onClick={() =>
                setShowAdvancedFilters(
                  !showAdvancedFilters
                )
              }
              style={{
                minWidth: isMobile
                  ? "100%"
                  : "160px",

                height: isMobile
                  ? "56px"
                  : "64px",

                border: "none",

                borderRadius: "999px",

                background:
                  "linear-gradient(135deg,#111827,#374151)",

                color: "#fff",

                fontWeight: "700",

                fontSize: "15px",

                cursor: "pointer",

                display: "flex",

                alignItems: "center",

                justifyContent:
                  "center",

                gap: "10px",

                boxShadow:
                  "0 12px 25px rgba(15,23,42,0.2)",
              }}
            >
              <FaFilter />
              Filters
            </motion.button>
          </div>

          {/* FILTER SECTION */}

          <AnimatePresence>
            {showAdvancedFilters && (
              <motion.div
                initial={{
                  opacity: 0,
                  height: 0,
                }}
                animate={{
                  opacity: 1,
                  height: "auto",
                }}
                exit={{
                  opacity: 0,
                  height: 0,
                }}
                style={{
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    marginTop: "22px",

                    display: "grid",

                    gridTemplateColumns:
                      isMobile
                        ? "1fr"
                        : "repeat(auto-fit,minmax(260px,1fr))",

                    gap: "16px",
                  }}
                >
                  {/* CATEGORY */}

                  <select
                    value={
                      selectedCategory
                    }
                    onChange={(e) =>
                      setSelectedCategory(
                        e.target.value
                      )
                    }
                    style={
                      modernFilterInput
                    }
                  >
                    {categories.map(
                      (cat) => (
                        <option
                          key={cat}
                        >
                          {cat}
                        </option>
                      )
                    )}
                  </select>

                  {/* MANUFACTURER */}

                  <select
                    value={
                      manufacturerFilter
                    }
                    onChange={(e) =>
                      setManufacturerFilter(
                        e.target.value
                      )
                    }
                    style={
                      modernFilterInput
                    }
                  >
                    <option value="">
                      All Companies
                    </option>

                    {[
                      ...new Set(
                        items.map(
                          (i) =>
                            i.manufacturer
                        )
                      ),
                    ].map((m) => (
                      <option
                        key={m}
                        value={m}
                      >
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ============================================ */}
        {/* GRID */}
        {/* ============================================ */}

        <div
          style={{
            display: "grid",

            gridTemplateColumns:
              "repeat(auto-fit,minmax(280px,1fr))",

            gap: isMobile
              ? "18px"
              : "25px",
          }}
        >
          {filteredItems.map(
            (item, index) => {
              if (
                filteredItems.length ===
                index + 1
              ) {
                return (
                  <div
                    ref={lastElementRef}
                    key={item.id}
                  >
                    <MedicineCard
                      item={item}
                      quantities={
                        quantities
                      }
                      handleQuantityChange={
                        handleQuantityChange
                      }
                      handleAddToCart={
                        handleAddToCart
                      }
                      orderNow={
                        orderNow
                      }
                      openMedicinePopup={() =>
                        setSelectedMedicine(
                          item
                        )
                      }
                    />
                  </div>
                );
              }

              return (
                <MedicineCard
                  key={item.id}
                  item={item}
                  quantities={
                    quantities
                  }
                  handleQuantityChange={
                    handleQuantityChange
                  }
                  handleAddToCart={
                    handleAddToCart
                  }
                  orderNow={orderNow}
                  openMedicinePopup={() =>
                    setSelectedMedicine(
                      item
                    )
                  }
                />
              );
            }
          )}
        </div>

        {/* LOADER */}

        {isLoading && (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              fontWeight: "800",
              fontSize: "20px",
            }}
          >
            Loading Medicines...
          </div>
        )}
      </div>

      {/* TOAST */}

      <AnimatePresence>
        {cartMessage && (
          <motion.div
            initial={{
              opacity: 0,
              y: 50,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: 50,
            }}
            style={toastStyle}
          >
            {cartMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* POPUP */}

      {selectedMedicine && (
        <MedicineDetailPopup
          medicine={
            selectedMedicine
          }
          onClose={() =>
            setSelectedMedicine(
              null
            )
          }
          handleAddToCart={
            handleAddToCart
          }
          orderNow={orderNow}
        />
      )}
    </>
  );
};

// ============================================
// CARD
// ============================================

const MedicineCard = ({
  item,
  handleAddToCart,
  orderNow,
  openMedicinePopup,
}) => {
  const [imageError, setImageError] =
    useState(false);

  const isAvailable =
    item.totalPiece > 0 &&
    !item.isDiscontinued;

  return (
    <motion.div
      whileHover={{
        y: -6,
      }}
      transition={{
        duration: 0.25,
      }}
      style={{
        background: "#fff",

        borderRadius: "28px",

        overflow: "hidden",

        padding: "18px",

        border:
          "1px solid #f1f5f9",

        boxShadow:
          "0 10px 35px rgba(15,23,42,0.06)",
      }}
    >
      {/* IMAGE */}

      <div
        onClick={
          openMedicinePopup
        }
        style={{
          cursor: "pointer",
        }}
      >
        {!imageError ? (
          <img
            src={item.image}
            alt={item.name}
            onError={() =>
              setImageError(true)
            }
            style={{
              width: "100%",
              height: "220px",
              objectFit: "cover",
              borderRadius: "22px",
            }}
          />
        ) : (
          <div
            style={{
              height: "220px",

              display: "flex",

              alignItems: "center",

              justifyContent:
                "center",

              flexDirection: "column",
            }}
          >
            <FaCapsules
              size={60}
              color="#94a3b8"
            />

            <p>No Image</p>
          </div>
        )}
      </div>

      {/* NAME */}

      <h3
        style={{
          marginTop: "18px",

          fontSize: "20px",

          fontWeight: "800",

          color: "#0f172a",

          lineHeight: "1.5",
        }}
      >
        {item.name}
      </h3>

      {/* META */}

      <div style={metaRow}>
        <FaIndustry color="#2563eb" />
        {item.manufacturer}
      </div>

      <div style={metaRow}>
        <FaPills color="#2563eb" />
        {item.type}
      </div>

      <div style={metaRow}>
        <FaBox color="#2563eb" />
        {item.packSize}
      </div>

      {/* STOCK */}

      <div
        style={{
          marginTop: "12px",

          color: isAvailable
            ? "#16a34a"
            : "#dc2626",

          fontWeight: "700",
        }}
      >
        {isAvailable ? (
          <>
            <FaCheckCircle />
            {" "}In Stock
          </>
        ) : (
          <>
            <FaTimesCircle />
            {" "}Out Of Stock
          </>
        )}
      </div>

      {/* PRICE */}

      <div
        style={{
          marginTop: "18px",
        }}
      >
        <div
          style={{
            textDecoration:
              "line-through",

            color: "#94a3b8",
          }}
        >
          ₹{item.actualPrice}
        </div>

        <div
          style={{
            fontSize: "32px",

            fontWeight: "900",

            color: "#16a34a",
          }}
        >
          ₹
          {item.discountedPrice}
        </div>
      </div>

      {/* BUTTONS */}

      <div
        style={{
          display: "flex",

          gap: "12px",

          marginTop: "22px",
        }}
      >
        <button
          onClick={() =>
            handleAddToCart(item)
          }
          style={cartBtn}
        >
          <FaShoppingCart />
          Cart
        </button>

        <button
          onClick={() =>
            orderNow(item)
          }
          style={buyBtn}
        >
          <FaBolt />
          Buy
        </button>
      </div>
    </motion.div>
  );
};

// ============================================
// STYLES
// ============================================

const modernFilterInput = {
  width: "100%",

  height: "58px",

  borderRadius: "18px",

  border: "2px solid #dbeafe",

  padding: "0 18px",

  outline: "none",

  fontSize: "15px",

  fontWeight: "600",

  background: "#ffffff",

  boxShadow:
    "0 5px 18px rgba(37,99,235,0.05)",
};

const cartBtn = {
  flex: 1,

  border: "none",

  height: "50px",

  borderRadius: "16px",

  background:
    "linear-gradient(135deg,#e0f2fe,#dbeafe)",

  color: "#0284c7",

  fontWeight: "700",

  cursor: "pointer",

  display: "flex",

  alignItems: "center",

  justifyContent: "center",

  gap: "8px",
};

const buyBtn = {
  flex: 1,

  border: "none",

  height: "50px",

  borderRadius: "16px",

  background:
    "linear-gradient(135deg,#2563eb,#3b82f6)",

  color: "#fff",

  fontWeight: "700",

  cursor: "pointer",

  display: "flex",

  alignItems: "center",

  justifyContent: "center",

  gap: "8px",

  boxShadow:
    "0 8px 18px rgba(37,99,235,0.25)",
};

const metaRow = {
  display: "flex",

  alignItems: "center",

  gap: "8px",

  marginTop: "10px",

  color: "#475569",

  fontSize: "14px",

  fontWeight: "500",
};

const toastStyle = {
  position: "fixed",

  bottom: "40px",

  left: "50%",

  transform: "translateX(-50%)",

  background:
    "linear-gradient(135deg,#16a34a,#22c55e)",

  color: "#fff",

  padding: "16px 30px",

  borderRadius: "999px",

  fontWeight: "700",

  zIndex: 9999,

  boxShadow:
    "0 12px 30px rgba(0,0,0,0.2)",
};

export default MedicinePage;